import {
  type PropertyLocationLayout,
  PropertyLocationSummary,
} from "@/components/property/property-location-summary";
import { Button } from "@/components/ui/button";
import { CardContent, CardFooter } from "@/components/ui/card";
import { FormFieldWrapper } from "@/components/ui/form-field-wrapper";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { SuggestionList } from "@/components/ui/suggestion-list";
import { useAppForm } from "@/components/ui/tanstack-form";
import { UserType } from "@/constants";
import { updateLicenseMutation } from "@/hera-api/client/@tanstack/react-query.gen";
import type {
  JurisdictionResponseSchema,
  PropertyResponseSchema,
} from "@/hera-api/client/types.gen";
import { usePlacesAutocomplete } from "@/hooks/use-places-autocomplete";
import { notifyLicenseDetailsChanged } from "@/lib/license-details-change-notification";
import { parseGoogleAddressComponents } from "@/lib/parse-google-address-components";
import { formatDateNoTimezone, getQueryErrorMessage } from "@/lib/utils";
import { useStore } from "@tanstack/react-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { RotateCcw, Save } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { renderQuestionField } from "../../registrant/form-utils/question-field-utils";
import { RegistrationCertificateDownloadButton } from "../registration-certificate-download-button";
import { PropertySchema } from "../schemas/validation-schemas";

interface PropertyCardContentProps {
  property: PropertyResponseSchema;
  jurisdiction?: JurisdictionResponseSchema;
  userType: UserType;
  licenseId: string;
  forceEdit?: boolean;
  onEnterEdit?: () => void;
  /** Hide jurisdiction when branding (e.g. logo) already shows it above the tile. */
  hideJurisdiction?: boolean;
  /** When false, omit the registration certificate download control. */
  showRegistrationCertificate?: boolean;
  /**
   * Inspection portal layout: address/APN on one row and Street View beside the
   * map on small screens so the tile takes less vertical space on a phone.
   */
  compact?: boolean;
  /** Earliest listing observation. Omitted on license property tiles. */
  firstSeen?: string | null;
}

interface EditablePropertyInfoProps {
  property: PropertyResponseSchema;
  jurisdiction?: JurisdictionResponseSchema;
  licenseId: string;
  onSave: () => void;
  onReset: () => void;
}

function EditablePropertyInfo({
  property,
  jurisdiction,
  licenseId,
  onSave,
  onReset,
}: EditablePropertyInfoProps) {
  const queryClient = useQueryClient();
  const firstInputRef = useRef<HTMLInputElement>(null);

  const updateLicense = useMutation({
    ...updateLicenseMutation(),
  });

  useEffect(() => {
    // Focus on the first input when the component mounts
    // Use setTimeout to ensure the component is fully rendered
    const timer = setTimeout(() => {
      if (firstInputRef.current) {
        firstInputRef.current.focus();
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const form = useAppForm({
    defaultValues: {
      address1: property.address1,
      address2: property.address2,
      city: property.city,
      state: property.state,
      zipcode: property.zipcode,
      apn: property.apn,
    },
    validators: {
      onSubmit: PropertySchema,
    },
    onSubmit: async ({ value }) => {
      try {
        await updateLicense.mutateAsync({
          path: { license_id: licenseId },
          body: {
            property: value,
          },
        });
        await notifyLicenseDetailsChanged(queryClient, licenseId);
        toast({
          title: "Success",
          description: "Property information updated successfully",
          variant: "secondary",
        });
        onSave();
      } catch (error) {
        toast({
          title: "Error",
          description: `${getQueryErrorMessage(error)}`,
          variant: "destructive",
        });
      }
    },
  });

  const address1Value = useStore(
    form.store,
    (state) => state.values.address1 ?? "",
  );
  const { suggestions: addressSuggestions } = usePlacesAutocomplete(
    address1Value,
    {
      includedRegionCodes: ["us"],
      includedPrimaryTypes: ["street_address"],
    },
  );

  const handleAddressSuggestionClick = async (
    suggestion: google.maps.places.AutocompleteSuggestion,
  ) => {
    const place = suggestion.placePrediction?.toPlace();
    if (!place) return;
    await place.fetchFields({ fields: ["addressComponents"] });
    if (!place?.addressComponents) return;
    const { address1, address2, city, state, zipcode } =
      parseGoogleAddressComponents(place.addressComponents);
    form.setFieldValue("address1", address1);
    form.validateField("address1", "change");
    form.setFieldValue("address2", address2);
    form.validateField("address2", "change");
    form.setFieldValue("city", city);
    form.validateField("city", "change");
    form.setFieldValue("state", state);
    form.validateField("state", "change");
    form.setFieldValue("zipcode", zipcode);
    form.validateField("zipcode", "change");
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await form.handleSubmit();
  };

  const handleReset = () => {
    form.reset();
    onReset();
  };

  return (
    <CardContent className="space-y-4">
      <form.AppForm>
        <form className="space-y-4" onSubmit={handleSave}>
          <div className="space-y-4">
            <form.AppField
              name="address1"
              validators={{
                onChange: PropertySchema.shape.address1,
              }}
            >
              {(field) => (
                <FormFieldWrapper field={field} label="Address">
                  <SuggestionList
                    closeOnBlur
                    suggestions={addressSuggestions}
                    renderSuggestion={(suggestion) => (
                      <span>{suggestion.placePrediction?.text?.text}</span>
                    )}
                    getSuggestionKey={(suggestion) =>
                      suggestion.placePrediction?.placeId ?? ""
                    }
                    onSuggestionSelect={handleAddressSuggestionClick}
                  >
                    <Input
                      ref={firstInputRef}
                      value={field.state.value ?? ""}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      placeholder="Enter street address"
                    />
                  </SuggestionList>
                </FormFieldWrapper>
              )}
            </form.AppField>

            <form.AppField
              name="address2"
              validators={{
                onChange: PropertySchema.shape.address2,
              }}
            >
              {(field) => (
                <FormFieldWrapper field={field} label="Address 2">
                  {renderQuestionField("text", field)}
                </FormFieldWrapper>
              )}
            </form.AppField>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <form.AppField
                name="city"
                validators={{
                  onChange: PropertySchema.shape.city,
                }}
              >
                {(field) => (
                  <FormFieldWrapper field={field} label="City">
                    {renderQuestionField("text", field)}
                  </FormFieldWrapper>
                )}
              </form.AppField>

              <form.AppField
                name="state"
                validators={{
                  onChange: PropertySchema.shape.state,
                }}
              >
                {(field) => (
                  <FormFieldWrapper field={field} label="State">
                    {renderQuestionField("text", field)}
                  </FormFieldWrapper>
                )}
              </form.AppField>

              <form.AppField
                name="zipcode"
                validators={{
                  onChange: PropertySchema.shape.zipcode,
                }}
              >
                {(field) => (
                  <FormFieldWrapper field={field} label="Zipcode">
                    {renderQuestionField("text", field)}
                  </FormFieldWrapper>
                )}
              </form.AppField>
            </div>

            <form.AppField
              name="apn"
              validators={{
                onChange: PropertySchema.shape.apn,
              }}
            >
              {(field) => (
                <FormFieldWrapper field={field} label="APN">
                  {renderQuestionField("text", field)}
                </FormFieldWrapper>
              )}
            </form.AppField>

            <div>
              <p className="text-sm font-bold">Jurisdiction</p>
              <p className="text-sm">{jurisdiction?.name ?? "Unknown"}</p>
            </div>
          </div>

          <CardFooter className="flex justify-between px-0">
            <Button
              type="button"
              variant="outline"
              onClick={handleReset}
              disabled={updateLicense.isPending}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset
            </Button>
            <form.Subscribe
              selector={(state) => [state.canSubmit, state.isSubmitting]}
            >
              {([canSubmit, isSubmitting]) => (
                <Button
                  type="submit"
                  disabled={!canSubmit || updateLicense.isPending}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {isSubmitting || updateLicense.isPending
                    ? "Saving..."
                    : "Save"}
                </Button>
              )}
            </form.Subscribe>
          </CardFooter>
          <div className="pt-4 border-t">
            <RegistrationCertificateDownloadButton
              licenseId={licenseId}
              className="w-full sm:w-auto"
            />
          </div>
        </form>
      </form.AppForm>
    </CardContent>
  );
}

interface ReadOnlyPropertyInfoProps {
  property: PropertyResponseSchema;
  jurisdiction?: JurisdictionResponseSchema;
  userType: UserType;
  licenseId: string;
  hideJurisdiction?: boolean;
  showRegistrationCertificate?: boolean;
  compact?: boolean;
  firstSeen?: string | null;
}

function ReadOnlyPropertyInfo({
  property,
  jurisdiction,
  userType,
  licenseId,
  hideJurisdiction = false,
  showRegistrationCertificate = true,
  compact = false,
  firstSeen = null,
}: ReadOnlyPropertyInfoProps) {
  const firstSeenLabel = firstSeen
    ? formatDateNoTimezone(firstSeen)
    : undefined;
  const layout: PropertyLocationLayout = compact
    ? "compact"
    : userType === UserType.OPERATOR
      ? "operator"
      : userType === UserType.REGISTRANT
        ? "registrant"
        : "stacked";

  return (
    <CardContent
      className={compact ? "space-y-3 px-4 md:space-y-4 md:px-6" : "space-y-4"}
    >
      <PropertyLocationSummary
        location={property}
        jurisdictionName={jurisdiction?.name}
        hideJurisdiction={hideJurisdiction}
        layout={layout}
        firstSeenLabel={firstSeenLabel}
      />
      {showRegistrationCertificate ? (
        <div className="pt-4 border-t">
          <RegistrationCertificateDownloadButton
            licenseId={licenseId}
            className="w-full sm:w-auto"
          />
        </div>
      ) : null}
    </CardContent>
  );
}

export function PropertyCardContent({
  property,
  jurisdiction,
  userType,
  licenseId,
  forceEdit,
  onEnterEdit,
  hideJurisdiction = false,
  showRegistrationCertificate = true,
  compact = false,
  firstSeen = null,
}: PropertyCardContentProps) {
  const [isEditing, setIsEditing] = useState(false);

  // Allow parent to trigger edit mode (for top-right header button)
  useEffect(() => {
    if (userType === UserType.OPERATOR && forceEdit && !isEditing) {
      setIsEditing(true);
      onEnterEdit?.();
    }
  }, [forceEdit, isEditing, onEnterEdit, userType]);

  if (userType === UserType.OPERATOR && isEditing) {
    return (
      <EditablePropertyInfo
        property={property}
        jurisdiction={jurisdiction}
        licenseId={licenseId}
        onSave={() => setIsEditing(false)}
        onReset={() => setIsEditing(false)}
      />
    );
  }

  return (
    <ReadOnlyPropertyInfo
      property={property}
      jurisdiction={jurisdiction}
      userType={userType}
      licenseId={licenseId}
      hideJurisdiction={hideJurisdiction}
      showRegistrationCertificate={showRegistrationCertificate}
      compact={compact}
      firstSeen={firstSeen}
    />
  );
}
